import React, { useEffect, useState, useContext } from 'react';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Button,
  Grid,
  TextField,
  InputAdornment,
  Alert,
  Autocomplete,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import { NAME_REGEX, GENDER_REGEX, USERNAME_REGEX, PHONE_REGEX, EMAIL_REGEX } from '../../utils/constants';
import axiosInstance from '../../api';
import StyledCardNoHeight from '../../components/card/StyledCardNoHeight';
import { authContext } from '../../routes';
import CustomSnackbar from '../../components/alerts/CustomSnackbar';
import SERVER_API_URL from '../../config';
import {
  EMAIL_EXCEPTION,
  GENDER_EXCEPTION,
  INTERNAL_SERVER_ERROR,
  NAME_EXCEPTION,
  PHONE_EXCEPTION,
  REQUIRED_FIELD,
  USERNAME_EXCEPTION,
} from '../../utils/exception';

export default function ProfileSettings({ user, canEdit }) {
  const [notification, setNotification] = useState({});
  const { getUserData } = useContext(authContext);

  const [disabled, setDisabled] = useState(false);
  const [firstDisabled, setFirstDisabled] = useState(true);

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [firstName, setFirstName] = useState(user && user.firstName);
  const [firstNameClicked, setFirstNameClicked] = useState(false);
  const [formErrorOnFirstName, setFormErrorOnFirstName] = useState(false);
  const [helperTextOnFirstName, setHelperTextOnFirstName] = useState(null);

  const [lastName, setLastName] = useState(user && user.lastName);
  const [lastNameClicked, setLastNameClicked] = useState(false);
  const [formErrorOnLastName, setFormErrorOnLastName] = useState(false);
  const [helperTextOnLastName, setHelperTextOnLastName] = useState(null);

  const [christianName, setChristianName] = useState(user && user.christianName);
  const [christianNameClicked, setChristianNameClicked] = useState(false);
  const [formErrorOnChristianName, setFormErrorOnChristianName] = useState(false);
  const [helperTextOnChristianName, setHelperTextOnChristianName] = useState(null);

  const [position, setPosition] = useState(user && user.position);
  const [positionClicked, setPositionClicked] = useState(false);
  const [formErrorOnPosition, setFormErrorOnPosition] = useState(false);
  const [helperTextOnPosition, setHelperTextOnPosition] = useState(null);

  const [username, setUsername] = useState(user && user.username);
  const [usernameClicked, setUsernameClicked] = useState(false);
  const [formErrorOnUsername, setFormErrorOnUsername] = useState(false);
  const [helperTextOnUsername, setHelperTextOnUsername] = useState(null);

  const [email, setEmail] = useState(user && user.email);
  const [emailClicked, setEmailClicked] = useState(false);
  const [formErrorOnEmail, setFormErrorOnEmail] = useState(false);
  const [helperTextOnEmail, setHelperTextOnEmail] = useState(null);

  const [phone, setPhone] = useState(user && user.phone.slice(4, 13));
  const [phoneClicked, setPhoneClicked] = useState(false);
  const [formErrorOnPhone, setFormErrorOnPhone] = useState(false);
  const [helperTextOnPhone, setHelperTextOnPhone] = useState(null);

  const [gender, setGender] = useState(user && user.gender);
  const [genderClicked, setGenderClicked] = useState(false);
  const [formErrorOnGender, setFormErrorOnGender] = useState(false);
  const [helperTextOnGender, setHelperTextOnGender] = useState(null);

  useEffect(() => {}, [getUserData]);

  const handleSave = async (event) => {
    event.preventDefault();
    setFormErrorSeverity('error');
    setDisabled(true);

    if (
      !formErrorOnFirstName &&
      !formErrorOnLastName &&
      !formErrorOnChristianName &&
      !formErrorOnPosition &&
      !formErrorOnUsername &&
      !formErrorOnEmail &&
      !formErrorOnPhone &&
      !formErrorOnGender
    ) {
      try {
        const response = await axiosInstance.put(`${SERVER_API_URL}/user/updateCredentialUserProfile/${user.id}`, {
          firstName,
          lastName,
          christianName,
          username,
          email,
          phone,
          position,
          gender,
        });

        if (response.status === 200) {
          getUserData();
          notification.message = 'መገለጫ በተሳካ ሁኔታ ተስተካክሏል።';
          notification.severity = 'success';
        }
      } catch (e) {
        notification.message = 'መገለጫን ማረም አልተሳካም። እባክዎ ዳግም ይሞክሩ!';
        notification.severity = 'error';
        if (e.response.status === 406) {
          setFormError('የተጠቃሚ ስም አስቀድሞ ተወስዷል!');
        } else if (e.response.status === 400) {
          setFormError(e.response.data.message);
        } else if (e.response.status === 500) {
          setFormError(INTERNAL_SERVER_ERROR);
        }
      }

      setNotification(notification);
    } else {
      setFormError('እባክዎ አስፈላጊዎቹን መስኮች ይሙሉ!');
    }

    setFirstDisabled(true);
    setDisabled(false);
  };

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    setFirstDisabled(false);
    setDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'firstName':
        if (value.length === 0) {
          setFirstNameClicked(false);
          setFormErrorOnFirstName(firstNameClicked && value.length === 0);
          setHelperTextOnFirstName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnFirstName(true);
          setHelperTextOnFirstName(NAME_EXCEPTION);
        } else {
          setHelperTextOnFirstName(null);
          setFormErrorOnFirstName(false);
        }
        break;
      case 'lastName':
        if (value.length === 0) {
          setLastNameClicked(false);
          setFormErrorOnLastName(lastNameClicked && value.length === 0);
          setHelperTextOnLastName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnLastName(true);
          setHelperTextOnLastName(NAME_EXCEPTION);
        } else {
          setHelperTextOnLastName(null);
          setFormErrorOnLastName(false);
        }
        break;
      case 'christianName':
        if (value.length === 0) {
          setChristianNameClicked(false);
          setFormErrorOnChristianName(christianNameClicked && value.length === 0);
          setHelperTextOnChristianName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnChristianName(true);
          setHelperTextOnChristianName(NAME_EXCEPTION);
        } else {
          setHelperTextOnChristianName(null);
          setFormErrorOnChristianName(false);
        }
        break;
      case 'gender':
        if (value.length === 0) {
          setGenderClicked(false);
          setFormErrorOnGender(genderClicked && value.length === 0);
          setHelperTextOnGender(REQUIRED_FIELD);
        } else if (!GENDER_REGEX.test(value)) {
          setFormErrorOnGender(true);
          setHelperTextOnGender(GENDER_EXCEPTION);
        } else {
          setHelperTextOnGender(null);
          setFormErrorOnGender(false);
        }
        break;
      case 'username':
        if (value.length === 0) {
          setUsernameClicked(false);
          setFormErrorOnUsername(usernameClicked && value.length === 0);
          setHelperTextOnUsername(REQUIRED_FIELD);
        } else if (!USERNAME_REGEX.test(value)) {
          setFormErrorOnUsername(true);
          setHelperTextOnUsername(USERNAME_EXCEPTION);
        } else {
          setHelperTextOnUsername(null);
          setFormErrorOnUsername(false);
        }
        break;
      case 'email':
        if (value.length === 0) {
          setEmailClicked(false);
          setFormErrorOnEmail(emailClicked && value.length === 0);
          setHelperTextOnEmail(REQUIRED_FIELD);
        } else if (!EMAIL_REGEX.test(value)) {
          setFormErrorOnEmail(true);
          setHelperTextOnEmail(EMAIL_EXCEPTION);
        } else {
          setHelperTextOnEmail(null);
          setFormErrorOnEmail(false);
        }
        break;
      case 'position':
        if (value.length === 0) {
          setPositionClicked(false);
          setFormErrorOnPosition(positionClicked && value.length === 0);
          setHelperTextOnPosition(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnPosition(true);
          setHelperTextOnPosition(NAME_EXCEPTION);
        } else {
          setHelperTextOnPosition(null);
          setFormErrorOnPosition(false);
        }
        break;
      case 'phone':
        if (value.length === 0) {
          setPhoneClicked(false);
          setFormErrorOnPhone(phoneClicked && value.length === 0);
          setHelperTextOnPhone(REQUIRED_FIELD);
        } else if (!PHONE_REGEX.test(value)) {
          setFormErrorOnPhone(true);
          setHelperTextOnPhone(PHONE_EXCEPTION);
        } else {
          setHelperTextOnPhone(null);
          setFormErrorOnPhone(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    setFirstDisabled(false);
    setDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'firstName':
        setFirstName(value);
        setFormErrorOnFirstName(false);
        setHelperTextOnFirstName(null);
        break;
      case 'lastName':
        setLastName(value);
        setFormErrorOnLastName(false);
        setHelperTextOnLastName(null);
        break;
      case 'christianName':
        setChristianName(value);
        setFormErrorOnChristianName(false);
        setHelperTextOnChristianName(null);
        break;
      case 'gender':
        setGender(value);
        setFormErrorOnGender(false);
        setHelperTextOnGender(null);
        break;
      case 'username':
        setUsername(value);
        setFormErrorOnUsername(false);
        setHelperTextOnUsername(null);
        break;
      case 'email':
        setEmail(value);
        setFormErrorOnEmail(false);
        setHelperTextOnEmail(null);
        break;
      case 'position':
        setPosition(value);
        setFormErrorOnPosition(false);
        setHelperTextOnPosition(null);
        break;
      case 'phone':
        setPhone(value);
        if (value.length === 0) {
          setPhoneClicked(false);
          setFormErrorOnPhone(phoneClicked && value.length === 0);
          setHelperTextOnPhone(REQUIRED_FIELD);
        } else if (!PHONE_REGEX.test(value)) {
          setFormErrorOnPhone(true);
          setHelperTextOnPhone(PHONE_EXCEPTION);
        } else {
          setHelperTextOnPhone(null);
          setFormErrorOnPhone(false);
        }
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setFormError(null);
    setFormErrorSeverity('error');

    setFirstDisabled(true);
    setDisabled(false);

    setFirstName(user && user.firstName);
    setLastName(user && user.lastName);
    setChristianName(user && user.christianName);
    setGender(user && user.gender);
    setPosition(user && user.position);
    setUsername(user && user.username);
    setEmail(user && user.email);
    setPhone(user && user.phone.slice(4, 13));

    setFirstNameClicked(false);
    setFormErrorOnFirstName(false);
    setHelperTextOnFirstName(null);

    setLastNameClicked(false);
    setFormErrorOnLastName(false);
    setHelperTextOnLastName(null);

    setChristianNameClicked(false);
    setFormErrorOnChristianName(false);
    setHelperTextOnChristianName(null);

    setGenderClicked(false);
    setFormErrorOnGender(false);
    setHelperTextOnGender(null);

    setUsernameClicked(false);
    setFormErrorOnUsername(false);
    setHelperTextOnUsername(null);

    setEmailClicked(false);
    setFormErrorOnEmail(false);
    setHelperTextOnEmail(null);

    setPositionClicked(false);
    setFormErrorOnPosition(false);
    setHelperTextOnPosition(null);

    setPhoneClicked(false);
    setFormErrorOnPhone(false);
    setHelperTextOnPhone(null);
  };

  return (
    <>
      {notification.message && <CustomSnackbar notification={notification} setNotification={setNotification} />}
      {user && (
        <StyledCardNoHeight component="form" autoComplete="off" onSubmit={handleSave} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ color: 'darkgray' }}>
            የመገለጫ ማስተካከያ
          </Typography>
          <Tooltip title={canEdit ? 'መገለጫህን ማዘመን አልተፈቀደልህም። ፍንጭ፡ እባክዎ አስተዳዳሪዎን ያነጋግሩ!' : ''}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12}>
                {formError && (
                  <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
                    {formError}
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  error={formErrorOnFirstName}
                  id="firstName"
                  name="firstName"
                  label="የመጀመሪያ ስም"
                  fullWidth
                  // variant="standard"
                  value={firstName}
                  onChange={handleFormValueChange}
                  helperText={formErrorOnFirstName ? helperTextOnFirstName : null}
                  onFocus={() => setFirstNameClicked(true)}
                  onBlur={handleFormValueBlur}
                  disabled={canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  error={formErrorOnLastName}
                  id="lastName"
                  name="lastName"
                  label="የአባት ስም"
                  fullWidth
                  // variant="standard"
                  value={lastName}
                  onChange={handleFormValueChange}
                  helperText={formErrorOnLastName ? helperTextOnLastName : null}
                  onFocus={() => setLastNameClicked(true)}
                  onBlur={handleFormValueBlur}
                  disabled={canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  error={formErrorOnChristianName}
                  id="christianName"
                  name="christianName"
                  label="የክርስትና ስም"
                  fullWidth
                  // variant="standard"
                  value={christianName}
                  onChange={handleFormValueChange}
                  helperText={formErrorOnChristianName ? helperTextOnChristianName : null}
                  onFocus={() => setChristianNameClicked(true)}
                  onBlur={handleFormValueBlur}
                  disabled={canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={['ወንድ', 'ሴት']}
                  value={gender}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={formErrorOnGender}
                      id="gender"
                      name="gender"
                      label="ጾታ"
                      // variant="standard"
                      value={gender}
                      onChange={handleFormValueChange}
                      helperText={formErrorOnGender ? helperTextOnGender : null}
                      onFocus={() => setGenderClicked(true)}
                      onBlur={handleFormValueBlur}
                      disabled={canEdit}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  error={formErrorOnPosition}
                  id="position"
                  name="position"
                  label="የቤተክርስትያን የስራ መደብ"
                  fullWidth
                  // variant="standard"
                  value={position}
                  onChange={handleFormValueChange}
                  helperText={formErrorOnPosition ? helperTextOnPosition : null}
                  onFocus={() => setPositionClicked(true)}
                  onBlur={handleFormValueBlur}
                  disabled={canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  error={formErrorOnUsername}
                  id="username"
                  name="username"
                  label="የተጠቃሚ ስም"
                  fullWidth
                  // variant="standard"
                  value={username}
                  onChange={handleFormValueChange}
                  helperText={formErrorOnUsername ? helperTextOnUsername : null}
                  onFocus={() => setUsernameClicked(true)}
                  onBlur={handleFormValueBlur}
                  disabled={canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  error={formErrorOnEmail}
                  id="email"
                  name="email"
                  placeholder="example@gmail.com"
                  label="ኢሜይል"
                  fullWidth
                  // variant="standard"
                  value={email}
                  onChange={handleFormValueChange}
                  helperText={formErrorOnEmail ? helperTextOnEmail : null}
                  onFocus={() => setEmailClicked(true)}
                  onBlur={handleFormValueBlur}
                  disabled={canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  error={formErrorOnPhone}
                  id="phone"
                  name="phone"
                  label="የስልክ ቁጥር"
                  fullWidth
                  // variant="standard"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+251</InputAdornment>,
                  }}
                  value={phone}
                  onChange={handleFormValueChange}
                  helperText={formErrorOnPhone ? helperTextOnPhone : null}
                  onFocus={() => setPhoneClicked(true)}
                  onBlur={handleFormValueBlur}
                  disabled={canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right' }}>
                <Button
                  color="error"
                  type="reset"
                  onClick={handleClearForm}
                  sx={{ marginLeft: 5, width: 150 }}
                  disabled={canEdit}
                >
                  <RefreshIcon /> ቅጽ ዳግም አስጀምር
                </Button>{' '}
                {disabled && (
                  <Button variant="contained" type="submit" sx={{ minWidth: 150 }} disabled={disabled || canEdit}>
                    <CircularProgress style={{ width: '20px', height: '20px' }} />
                    በማስቀመጥ ላይ ...
                  </Button>
                )}
                {!disabled && (
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{ minWidth: 150 }}
                    startIcon={<SaveTwoToneIcon />}
                    disabled={disabled || firstDisabled || canEdit}
                  >
                    ለውጦችን አስቀምጥ
                  </Button>
                )}
              </Grid>
            </Grid>
          </Tooltip>
        </StyledCardNoHeight>
      )}
    </>
  );
}

ProfileSettings.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    christianName: PropTypes.string.isRequired,
    position: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
};
